import type { EventListPageTypeString } from '~/composables/types/strapiEventListPage'
import type { MediaItem } from '~/composables/types/event'

export interface TransactionalPageReview {
  object: string
  id: string
  rating: number
  customer: string
  created_at: string
  review: string
  verified: boolean
  media?: { data: MediaItem }
  reviewMedia?: { data: MediaItem[] }
  item: string
  item_type?: string

  review_id: string
  reviewee_name: string
  review_date: string
  review_text: string
  event_title: string
  event_price: string
  event_permalink: string
  event_location: string
  event_avg_review: string
  event_total_reviews: number
}

interface FetchResponse {
  data: TransactionalPageReview[] | null
  meta: {
    custom: any[]
    include: string[]
  }
}

const getPageType = {
  city: 'Locality',
  teamevent: 'Tag',
  'city-category': 'LocalityCategory',
  general: 'Tag',
  category: 'Category',
  kits: 'Kits',
} as { [key: string]: string }

export const useTransactionalPageReviews = async (
  pageType: EventListPageTypeString,
  slug?: string,
  language: string = 'de-de'
) => {
  const config = useRuntimeConfig()
  const { apiBaseURL } = config.public

  const formattedPageType = getPageType[pageType.toLowerCase()] || 'Tag'

  const acceptedLanguage = language === 'de-de' ? 'de_DE' : 'en_US'

  const getTransactionalPageReviews = async (): Promise<
    TransactionalPageReview[]
  > => {
    // only fetch when we have a slug
    if (!slug) {
      return []
    }

    try {
      const reviews = await $fetch<FetchResponse>(
        `/v1/store/reviews/transactional-page/?include=media,reviewMedia&page=${formattedPageType}&slug=${slug}`,
        {
          method: 'GET',
          baseURL: apiBaseURL,
          headers: {
            'Accept-Language': acceptedLanguage,
          },
        }
      )

      // Ensure we return an array
      return Array.isArray(reviews?.data)
        ? (reviews?.data as TransactionalPageReview[])
        : []
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const {
    data: reviews,
    pending,
    error,
    refresh,
  } = await useAsyncData(
    // include language in the key so SSR caches per-locale
    `transactional-page-reviews-${pageType}-${slug}-${acceptedLanguage}`,
    () => getTransactionalPageReviews(),
    {
      default: () => [] as TransactionalPageReview[],
      server: true,
      dedupe: 'defer',
    }
  )

  return {
    reviews,
    pending: readonly(pending),
    error: readonly(error),
    refresh,
  }
}
